"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";

export default function VideoCarousel() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [touchStartX, setTouchStartX] = useState(null);
    const t = useTranslations("VideoCarousel");

    const videos = [
        {
            id: 1,
            title: t("video1.title"),
            thumbnail: "https://i3.ytimg.com/vi/Krs4exQhwJ0/maxresdefault.jpg",
            url: "https://youtu.be/Krs4exQhwJ0",
            views: "150K",
            category: t("video1.category"),
            accent: "#3B82F6",
        },
        {
            id: 2,
            title: t("video2.title"),
            thumbnail: "https://i3.ytimg.com/vi/RMe5LYEnfy8/maxresdefault.jpg",
            url: "https://youtu.be/RMe5LYEnfy8",
            views: "10K",
            category: t("video2.category"),
            accent: "#06B6D4",
        },
        {
            id: 3,
            title: t("video3.title"),
            thumbnail: "https://i3.ytimg.com/vi/beI4cOGDVDo/maxresdefault.jpg",
            url: "https://youtu.be/beI4cOGDVDo",
            views: "15K",
            category: t("video3.category"),
            accent: "#8B5CF6",
        },
    ];

    const DURATION_MS = 4000;
    const TICK_MS = 50;

    useEffect(() => {
        if (isPaused || isTransitioning) return;
        const interval = setInterval(() => {
            setProgress((prev) => {
                const next = prev + (100 * TICK_MS) / DURATION_MS;
                if (next >= 100) {
                    setIsTransitioning(true);
                    setTimeout(() => {
                        setCurrentIndex(
                            (prevIdx) => (prevIdx + 1) % videos.length,
                        );
                        setIsTransitioning(false);
                    }, 300);
                    return 0;
                }
                return next;
            });
        }, TICK_MS);
        return () => clearInterval(interval);
    }, [isPaused, isTransitioning]);

    const handleVideoClick = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const goToVideo = (index) => {
        if (index !== currentIndex && !isTransitioning) {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentIndex(index);
                setIsTransitioning(false);
            }, 300);
            setProgress(0);
        }
    };

    const goNext = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % videos.length);
            setIsTransitioning(false);
        }, 300);
        setProgress(0);
    };

    const goPrev = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentIndex(
                (prev) => (prev - 1 + videos.length) % videos.length,
            );
            setIsTransitioning(false);
        }, 300);
        setProgress(0);
    };

    return (
        <div className="w-full max-w-[500px] mx-auto space-y-6">
            {/* Header élégant */}
            <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t("title")}
                </h3>
                <p className="text-sm text-gray-500 dark:text-grayDark-400">
                    {t("description")}
                </p>
            </div>

            {/* Stack verticale avec cartes qui changent */}
            <div
                className="relative w-full pb-[56.25%] outline-hidden"
                tabIndex={0}
                role="region"
                aria-label="Carrousel vidéos populaires"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
                onKeyDown={(e) => {
                    if (e.key === "ArrowRight") {
                        e.preventDefault();
                        goNext();
                    } else if (e.key === "ArrowLeft") {
                        e.preventDefault();
                        goPrev();
                    }
                }}
                onTouchStart={(e) =>
                    setTouchStartX(e.touches[0]?.clientX ?? null)
                }
                onTouchEnd={(e) => {
                    const endX = e.changedTouches[0]?.clientX ?? 0;
                    const startX = touchStartX ?? 0;
                    const deltaX = endX - startX;
                    if (Math.abs(deltaX) > 40) {
                        deltaX < 0 ? goNext() : goPrev();
                    }
                    setTouchStartX(null);
                }}
            >
                {/* Carte active */}
                <div
                    className={`absolute inset-0 transition-all duration-500 ease-out ${
                        isTransitioning
                            ? "opacity-0 -translate-y-4 scale-95"
                            : "opacity-100 translate-y-0 scale-100"
                    }`}
                >
                    <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-grayDark-900 shadow-lg border border-gray-100 dark:border-grayDark-800 h-full">
                        {/* Barre de progression */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
                            <div
                                className="h-full"
                                style={{
                                    width: `${progress}%`,
                                    backgroundColor:
                                        videos[currentIndex].accent,
                                }}
                            />
                        </div>

                        <img
                            src={videos[currentIndex].thumbnail}
                            alt={videos[currentIndex].title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                        {/* Bouton play centré - visible seulement au hover */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                            <button
                                onClick={() =>
                                    handleVideoClick(videos[currentIndex].url)
                                }
                                className="w-16 h-16 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 hover:bg-white group/btn"
                                aria-label={`Lire ${videos[currentIndex].title}`}
                            >
                                <svg
                                    className="w-7 h-7 text-gray-800 ml-1 group-hover/btn:text-blue-600 transition-colors duration-300"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            </button>
                        </div>

                        {/* Contenu overlay */}
                        <div className="absolute bottom-4 left-4 right-4">
                            <div className="flex items-center space-x-2 mb-2">
                                <div
                                    className="w-2 h-2 rounded-full"
                                    style={{
                                        backgroundColor:
                                            videos[currentIndex].accent,
                                    }}
                                />
                                <span className="text-xs font-bold text-white uppercase tracking-wider drop-shadow-lg">
                                    {videos[currentIndex].category}
                                </span>
                            </div>
                            <h3 className="text-lg font-bold text-white leading-tight mb-2 drop-shadow-lg">
                                {videos[currentIndex].title}
                            </h3>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 text-white text-sm">
                                    <svg
                                        className="w-4 h-4 drop-shadow-lg"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                                    </svg>
                                    <span className="font-bold drop-shadow-lg">
                                        {videos[currentIndex].views}
                                    </span>
                                </div>
                                <button
                                    onClick={() =>
                                        handleVideoClick(
                                            videos[currentIndex].url,
                                        )
                                    }
                                    className="bg-white/20 backdrop-blur-sm text-white py-2 px-4 rounded-lg font-bold transition-all duration-300 hover:bg-white/30 drop-shadow-lg"
                                >
                                    {t("watch")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Indicateurs de navigation */}
            <div className="flex items-center justify-center space-x-3">
                {videos.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToVideo(index)}
                        disabled={isTransitioning}
                        aria-label={`Aller à la vidéo ${index + 1}`}
                        className={`transition-all duration-300 rounded-full ${
                            index === currentIndex
                                ? "w-8 h-2 opacity-100"
                                : "w-2 h-2 opacity-30 hover:opacity-60"
                        } ${
                            isTransitioning
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                        }`}
                        style={{
                            backgroundColor:
                                index === currentIndex
                                    ? videos[currentIndex].accent
                                    : "#9CA3AF",
                        }}
                    />
                ))}
            </div>

            {/* Miniatures des autres vidéos */}
            <div className="grid grid-cols-3 gap-3">
                {videos.map((video, index) => (
                    <div
                        key={video.id}
                        className={`group cursor-pointer transition-all duration-300 rounded-lg overflow-hidden ${
                            index === currentIndex ? "border-2" : ""
                        }`}
                        style={{
                            borderColor:
                                index === currentIndex
                                    ? video.accent
                                    : undefined,
                        }}
                        onClick={() => goToVideo(index)}
                    >
                        <div className="relative overflow-hidden bg-white dark:bg-grayDark-900 shadow-sm pb-[56.25%]">
                            <img
                                src={video.thumbnail}
                                alt={video.title}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300" />

                            {/* Indicateur de sélection */}
                            {index === currentIndex && (
                                <div className="absolute top-1 right-1">
                                    <div
                                        className="w-2 h-2 rounded-full"
                                        style={{
                                            backgroundColor: video.accent,
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
